import { AppConstants } from '@utils/app.constants';
import { Resource } from '@resources/resource';

export class KeyedResource extends Resource {
  private m_busClassName: string;
  private m_viewKeyName: string;
  private m_keyValueArray: any; // todo: type me
  private m_contextValueArray: any[];
  private m_staticContextValueArray: any[];
  private m_staticContextValueMap: Object;
  private m_keyAttributesArray: any[];
  private m_keyAttributesMap: Object;
  private m_uiResourceName: string;

  constructor(args?: any, uiResourceName?: string) {
    super(args.resourceType, args.resourceId);

    this.m_busClassName = args.busClassName || null;
    this.m_viewKeyName = args.viewKeyName || null;
    this.m_keyValueArray = args.keyValueArray || [];
    this.m_contextValueArray = args.contextValueArray || [];
    this.m_staticContextValueArray = args.staticContextValueArray || [];
    this.m_staticContextValueMap = {};
    this.m_keyAttributesArray = args.keyAttributesArray || [];
    this.m_keyAttributesMap = {};
    this.m_uiResourceName = uiResourceName;

    let buildResourceId = !this.resourceId;

    if (this.resourceId) {
      this.parseResourceId();

      // -------------------------------------------------------------
      // if resource is has field names in the keys then treat it
      // as a template and replace the name with values from the
      // dataView.
      // -------------------------------------------------------------
      if (args.dataView) {
        this.m_keyValueArray = this.keyNamesToKeyValues(this.m_keyValueArray, args.dataView);
        buildResourceId = true;
      }

      if (args.viewKeyName) {
        this.m_viewKeyName = args.viewKeyName;
        buildResourceId = true;
      }

      if (args.keyValueArray) {
        this.m_keyValueArray = args.keyValueArray;
        buildResourceId = true;
      }

      // -------------------------------------------------------------
      // This can be set in conjunction with a resourceId to obtian
      // a new Resource. For example a viewKeyResourceId + a
      // actionName.
      // -------------------------------------------------------------
      if (uiResourceName) {
        this.m_uiResourceName = uiResourceName;
        buildResourceId = true;
      }

      if (args.contextValueArray) {
        this.m_contextValueArray = args.contextValueArray;
        buildResourceId = true;
      }

      if (args.staticContextValueArray) {
        this.m_staticContextValueArray = args.staticContextValueArray;
        buildResourceId = true;
      }

      if (args.keyAttributesArray) {
        this.m_keyAttributesArray = args.keyAttributesArray;
        buildResourceId = true;
      }
    }

    // -------------------------------------------------------------
    // if resourceId && uiResourceName then build the resourceId
    // again so that the 2 will be merged. This is required
    // for the case where a resourceId is a viewKey resourceId
    // and will be used in conjunction with a uiResourceName
    // (like a formName, listName, actionName, etc...).
    // -------------------------------------------------------------
    if (buildResourceId) {
      this.buildResourceId();
    }
  }

  public get busClassName() {
    return this.m_busClassName;
  }

  public get uiResourceName() {
    return this.m_uiResourceName;
  }

  public get contextValueArray() {
    return this.m_contextValueArray;
  }

  public get staticContextValueArray() {
    return this.m_staticContextValueArray;
  }

  public get keyValueArray() {
    return this.m_keyValueArray;
  }

  public get dataResourceId(): string {
    return this.buildDataResourceId();
  }

  private buildDataResourceId(): string {
    const fullResourceId = this.resourceId;
    const index = fullResourceId.lastIndexOf('.' + this.m_uiResourceName);
    if (index === -1) {
      return fullResourceId;
    }

    return fullResourceId.substring(0, index);
  }

  // tslint:disable-next-line:no-unused-variable - method name not found
  private setContextValuesFromTemplate(contextFieldsArr: Array<any>, searchFields: any, header: any) {
    const keyValueArray: Array<any> = [];

    for (let i = 0; i < contextFieldsArr.length; i++) {
      const keyField = header.fields[ contextFieldsArr[ i ] ];

      if (!keyField) {
        keyValueArray.push(null);
      } else if (keyField && keyField.type === 'group') {
        const delim = keyField.delimiter || '';
        let val = '';

        for (let j = 0; j < keyField.members.length; j++) {
          const mem = keyField.members[ j ];
          if (!searchFields[ mem ]) {
            continue;
          }

          val = val + searchFields[ mem ];

          if (j !== keyField.members.length - 1) {
            val = val + delim;
          }
        }

        if (val !== '') {
          keyValueArray.push(null);
        }
      } else {
        if (searchFields[ contextFieldsArr[ i ] ] && searchFields[ contextFieldsArr[ i ] ].value) {
          keyValueArray.push(searchFields[ contextFieldsArr[ i ] ].value);
        }
      }

    }

    return keyValueArray;
  }

  private keyNamesToKeyValues(keyNameArray: Array<any>, dataView: any) {
    const keyValueArray: Array<any> = [];

    if (!keyNameArray) {
      return keyNameArray;
    }

    for (let i = 0; i < keyNameArray.length; i++) {
      if (dataView.cubeInfo) {
        if (keyNameArray[i].indexOf(':Date') > -1) {
          keyValueArray.push(dataView.cubeInfo.periodDate);
        } else {
          const keyName = 'frc.' + keyNameArray[i];
          keyValueArray.push((dataView.cubeInfo.hasOwnProperty(keyName)) ? dataView.cubeInfo[keyName] : null);
        }
      } else {
        const keyField = dataView.fields[ keyNameArray[ i ] ];
        if (keyField) {
          keyValueArray.push(keyField.value);
        } else {
          keyValueArray.push(null);
        }
      }
    }

    return keyValueArray;
  }

  private parseResourceId() {
    let fromIndex = 0;

    fromIndex = this.parseBusClassName(fromIndex);
    fromIndex = this.parseViewKeyName(fromIndex);
    fromIndex = this.parseKeyValues(fromIndex);

    if (fromIndex < this.resourceId.length) {
      if (this.resourceId.charAt(fromIndex) === '.') {
        fromIndex++;
      }

      if (fromIndex < this.resourceId.length) {
        this.m_uiResourceName = this.resourceId.substring(fromIndex);
      }
    }
  }

  private parseBusClassName(fromIndex: number) {
    let index = this.resourceId.indexOf('(', fromIndex);

    if (index > -1) {
      const busViewName = this.resourceId.substring(0, index);
      const bracketIndex = busViewName.indexOf('[', fromIndex);

      if (bracketIndex > -1) {
        this.m_busClassName = this.resourceId.substring(0, bracketIndex);
        return bracketIndex;
      } else {
        this.m_busClassName = busViewName;
        return index;
      }
    }

    index = this.resourceId.indexOf(AppConstants.RESOURCE_SPEC_DELIMITER, fromIndex);
    if (index > -1) {
      const busViewName = this.resourceId.substring(0, index);
      const bracketIndex = busViewName.indexOf('[', fromIndex);

      if (bracketIndex > -1) {
        this.m_busClassName = this.resourceId.substring(0, bracketIndex);
        return bracketIndex;
      } else {
        this.m_busClassName = busViewName;
        return index;
      }
    } else {
      this.m_busClassName = this.resourceId;
      return this.resourceId.length;
    }
  }

  private parseViewKeyName(fromIndex: number) {
    if (fromIndex >= this.resourceId.length) {
      return fromIndex;
    }

    if (this.resourceId.charAt(fromIndex) !== '[') {
      return fromIndex;
    }

    const beginIndex = fromIndex;

    /*
     * Cannot have a viewKeyName w/o a keyValueArray so look for ']('
     * as the end of the viewKeyName string.
     */
    const endIndex = this.resourceId.indexOf(']', beginIndex + 1);
    if (endIndex === -1) {

      return fromIndex;
    }

    this.m_viewKeyName = this.resourceId.substring(beginIndex + 1, endIndex);
    return endIndex + 1;

    // var beginIndex = this.resourceId.indexOf('[', fromIndex);
    // if (beginIndex == -1)
    // 	return fromIndex;
    //
    // var endIndex = this.resourceId.indexOf(']', beginIndex + 1);
    //
    // if (endIndex == -1)
    // 	return fromIndex;
    //
    // this.viewKeyName = this.resourceId.substring(beginIndex + 1, endIndex);
    // return endIndex + 1;
  }

  // private encodeFirstAndLastViewKeys(key)
  // {
  //    if (key == undefined)
  //        return key;
  //
  //    var res = key.split('(');
  //    var openParenIndex = key.indexOf('(');
  //    if (openParenIndex === -1)
  //        return openParenIndex;
  //
  //    var closeParenIndex = key.lastIndexOf(')');
  //    if (closeParenIndex == -1)
  //        throw 'Invalid resource id - open paren requires a close paren';
  //
  //    var keyString = key.substring(openParenIndex + 1, closeParenIndex);
  //
  //    var keyArr = [];
  //    keyArr = keyString.split(',');
  //
  //    for(var z = 0; z < keyArr.length; z++)
  //    {
  //        if(keyArr[z].indexOf('|') > -1)
  //        {
  //            keyArr[z] = '{' + keyArr[z] + '}';
  //        }
  //    }
  //
  //    return res[0] + '(' + keyArr.join(',') + ')';
  // }

  private parseKeyValues(fromIndex: number) {
    const openParenIndex = this.resourceId.indexOf('(', fromIndex);

    if (openParenIndex === -1) {
      return fromIndex;
    }

    // let closeParenIndex = this.resourceId.lastIndexOf(')');
    const aaIndex = this.resourceId.indexOf('.actor.agent(', fromIndex);
    const closeParenIndex = aaIndex > -1 ? this.resourceId.lastIndexOf(')', aaIndex) : this.resourceId.lastIndexOf(')');
    if (closeParenIndex === -1) {
      throw 'Invalid resource id - open paren requires a close paren';
    }

    const keyString = this.resourceId.substring(openParenIndex + 1, closeParenIndex);

    const keyValueList: Array<any> = [];
    const businessClassContextValueList: Array<any> = [];
    const staticContextValueList: Array<any> = [];
    let beginIndex = 0; let beginEscape = -1;
    const keyStringLength = keyString.length;
    let currentValueList = keyValueList;

    for (let i = 0, len = keyStringLength; i < len; i++) {
      const currentChar = keyString.charAt(i);

      if (currentChar === '{' && beginEscape === -1) {
        beginEscape = i;
        continue;
      }

      if (currentChar === '}' && beginEscape > -1) {
        currentValueList.push(keyString.substring(beginEscape + 1, i));

        // Do not skip the next iteration since the current value list may or may not change. Consider the following examples
        // GeneralLedgerChartAccount(SCB,{2_PPE,NET}:_cv_,_cv_MAIN_2,_cv_,_cv_false,_cv_,_cv_TEST,_cv_false,_cv_false,null)
        // GeneralLedgerChartAccount(SCB,{2_PPE,NET},NEXTKEY:_cv_,_cv_MAIN_2,_cv_,_cv_false,_cv_,_cv_TEST,_cv_false,_cv_false,null)
        // CandidateTagCode(7004,{[S,M,L,XL] SIZE})
        // GeneralLedgerChartAccount(SCB,{2_PPE,NET},NEXTKEY:_cv_,_cv_MAIN_2,_cv_,_cv_false,_cv_,_cv_TEST,_cv_false,_cv_false,{nu,ll})
        // i++; // Commented the increment statement. Akila (LMCLIENT-21602)
        beginEscape = -1;
        beginIndex = (i + 1 < keyStringLength) ? (i + 2) : (i + 1);
        continue;
      }

      if (beginEscape === -1) {
        if (currentChar === AppConstants.RESOURCE_KEY_DELIMITER ||
            currentChar === AppConstants.RESOURCE_KEY_SECTION_DELIMITER_COLON ||
            currentChar === AppConstants.RESOURCE_KEY_SECTION_DELIMITER_PIPE) {
          // Again consider GeneralLedgerChartAccount(SCB,{2_PPE,NET}:_cv_,_cv_MAIN_2,_cv_,_cv_false,_cv_,_cv_TEST,_cv_false,_cv_false,null)
          // beginIndex is 16 and i is 15. Do nothing here. This will help switch the current list to business class context keys.
          // Consider GeneralLedgerChartAccount(SCB,2_FIXEDASSET:null,MAIN_2,,null,null,TEST,null,null,null)
          // With <= in the below condition, I will be able to push the empty value in bus class context keys (2 consecutive commas)
          if (beginIndex <= i) {
            currentValueList.push(keyString.substring(beginIndex, i));
            beginIndex = i + 1;
          }
        }

        if (currentChar === AppConstants.RESOURCE_KEY_SECTION_DELIMITER_COLON) {
          // ------------------------------------------------------------------
          // if colon delimiter then switch to the business class context
          // field value list.
          // ------------------------------------------------------------------
          currentValueList = businessClassContextValueList;
        }

        if (currentChar === AppConstants.RESOURCE_KEY_SECTION_DELIMITER_PIPE) {
          // Get the static context keys after '|' delimiter'
          currentValueList = staticContextValueList;
        }
      }
    }

    // Use cases:
    // 1) CandidateTagCode(7004,{[S,M,L,XL] SIZE}).Hello
    // 2) CandidateTagCode(7004,).Hello
    // 3) CandidateTagCode(7004,123|staticContextField=).Hello
    // 4) CandidateTagCode(7004:).Hello
    // 5) CandidateTagCode(7004,testing).Hello
    // 6) ReportDistributionGroup().ReportDistributionGroupCompositeForm
    if (beginIndex < keyStringLength 
    || (beginIndex === keyStringLength && keyString?.charAt(keyStringLength -1) === AppConstants.RESOURCE_KEY_DELIMITER)) {
      currentValueList.push(keyString.substring(beginIndex, keyStringLength));
    }

    // this.keys = keyString.split(RESOURCE_KEY_DELIMETER);
    this.m_keyValueArray = keyValueList;
    this.m_contextValueArray = businessClassContextValueList;

    if (staticContextValueList.length > 0) {
      for (let j = 0, len: number = staticContextValueList.length; j < len; j++) {
        const entry: string = staticContextValueList[ j ];
        const index = entry.indexOf('=');
        if (entry.startsWith('attr_prd_')) {
          this.m_keyAttributesArray.push(entry);
          this.m_keyAttributesMap[ entry.substring('attr_prd_'.length, index) ] = entry.substring(index + 1);
        } else {
          this.m_staticContextValueArray.push(entry);
          this.m_staticContextValueMap[ entry.substring(0, index) ] = entry.substring(index + 1);
        }
      }
    }


    return closeParenIndex + 1;
  }

  public generateResourceId(encode: boolean): string {
    return this._buildResourceId(encode);
  }

  private buildResourceId() {
    this.resourceId = this._buildResourceId(true);
  }

  private encodeValue(value: any, staticContextKey: boolean = false, encode: boolean = true) {
    let escape = false; let encodedValue = '';

    if (typeof value !== 'string') {
      return value;
    }

    if ((value.indexOf(AppConstants.RESOURCE_KEY_DELIMITER) > -1
      || value.indexOf(AppConstants.RESOURCE_KEY_SECTION_DELIMITER_COLON) > -1
      || value.indexOf(AppConstants.RESOURCE_KEY_SECTION_DELIMITER_PIPE) > -1)
      && !staticContextKey) {
      escape = true;
    }

    if (escape) {
      encodedValue += encode ? encodeURIComponent('{') : '{';
    }

    // encodeURIComponent is missing some things so do those after. - Phillip 11/20/2014
    // not sure about parens, but single quote needs encoding forsure...
    // http://jira.lawson.com/browse/LMCLIENT-6982
    // encodedValue += encodeURIComponent(value).replace(/[!'()*]/g, function(c) {
    //   return '%' + c.charCodeAt(0).toString(16);
    // });


    encodedValue += encode ? encodeURIComponent(value).replace(/[!'()*]/g, function(c) {
      return '%' + c.charCodeAt(0).toString(16);
    }) : value;

    if (escape) {
      encodedValue += encode ? encodeURIComponent('}') : '}';
    }
    
    return encodedValue;
  }

  private _buildResourceId(encode: boolean): string {
    if (this.resourceType === AppConstants.RESOURCETYPE_BIDRILL) {
      // Do not attempt to build BIDRILL resource id. It does not fit the pattern.
      return encode ? encodeURIComponent(this.resourceId) : this.resourceId;
    }
    let resourceId: string = null;

    if (!this.m_busClassName) {
      throw Error('busClassName is required');
    }

    resourceId = this.m_busClassName;

    if (this.m_viewKeyName) {
      resourceId += (encode)
        ? encodeURIComponent('[') + this.m_viewKeyName + encodeURIComponent(']')
        : '[' + this.m_viewKeyName + ']';
    }

    if (this.m_keyValueArray.length > 0
      || this.m_contextValueArray.length > 0
      || this.m_staticContextValueArray.length > 0
      || this.m_keyAttributesArray.length > 0) {
      resourceId += '('; // encodeURIComponent skips '(', so leave this alone. lmUrlUtilService will encode parens after it encodes plus in Angular. Not a problem in 3.5

      if (this.m_keyValueArray.length > 0) {
        for (let i = 0, length = this.m_keyValueArray.length; i < length; i++) {
          resourceId += (i > 0) ? (encode ? encodeURIComponent(',') : ',') : ''; 
          resourceId += this.encodeValue(this.m_keyValueArray[ i ], false, encode);
        }
      }

      if (this.m_contextValueArray.length > 0) {
        resourceId += (encode) ? encodeURIComponent(':') : ':';
        for (let i = 0, length = this.m_contextValueArray.length; i < length; i++) {
          resourceId += (i > 0) ? (encode ? encodeURIComponent(',') : ',') : ''; 
          resourceId += this.encodeValue(this.m_contextValueArray[ i ], false, encode);
        }
      }

      if (this.m_staticContextValueArray.length > 0 || this.m_keyAttributesArray.length > 0) {
        resourceId += (encode) ? encodeURIComponent('|') : '|';
      
        if (this.m_staticContextValueArray.length > 0) {
          resourceId += this.buildStaticContextOrKeyAttributeArray('', encode, this.m_staticContextValueArray);
        }

        if (this.m_keyAttributesArray.length > 0) {
          resourceId += this.buildStaticContextOrKeyAttributeArray('attr_prd_', encode, this.m_keyAttributesArray);
        }
      }

      resourceId += ')'; // encodeURIComponent skips ')', so leave this alone. lmUrlUtilService will encode parens after it encodes plus in Angular. Not a problem in 3.5
    }

    if (this.uiResourceName) {
      resourceId += '.' + this.uiResourceName;
    }

    return resourceId;
  }

  private buildStaticContextOrKeyAttributeArray(prefix: string, encode: boolean, inputArray: Array<string>): string {
    let outputString = '';

    for (let i = 0, length = inputArray.length; i < length; i++) {
      const keyValue = inputArray[ i ];
      const keyValueIndex = keyValue.split('=');
      const escape = keyValue.indexOf(',') > -1 || keyValue.indexOf(':') > -1 || keyValue.indexOf('|') > -1;
      
      if ( i > 0 )
        outputString += encode ? encodeURIComponent(',') : ',';
      
      if (escape)
        outputString += encode ? encodeURIComponent('{') : '{';

      outputString += prefix + keyValueIndex[0] + (encode ? encodeURIComponent('=') : '=') + this.encodeValue(keyValueIndex[ 1 ], true, encode);
      
      if (escape)
        outputString += encode ? encodeURIComponent('}') : '}';

      }
    return outputString;
  }

  // private buildStaticContextOrKeyAttributeArray2(prefix: string, encode: boolean, inputMap: Object): Array<string> {
  //   const outputList: Array<any> = Object.keys(inputMap).map(fieldId => {
  //     const value = inputMap[ fieldId ];
  //     if (!encode) {
  //       return prefix + fieldId + '=' + value;
  //     }
  //
  //     if (value.indexOf(',') > -1
  //       || value.indexOf(':') > -1
  //       || value.indexOf('|') > -1
  //     ) {
  //       return encodeURIComponent('{')  + prefix + fieldId + encodeURIComponent('=')
  //         + this.encodeValue(inputMap[ fieldId ], true) + encodeURIComponent('}') ;
  //     }
  //
  //     return prefix + fieldId + encodeURIComponent('=') + this.encodeValue(inputMap[ fieldId ], true);
  //   });
  //
  //   return outputList;
  // }
}
